import { Button, Grid, Stack, TextField, Typography } from "@mui/material";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { EPaymentIframes } from "../interface/IPayment";
import "./css/PaymentBalanceIframe.scss";
import { usePaymentBalanceIframe } from "./hook/usePaymentBalanceIframe";
import { priceToCents } from "itsf-ui-common";
import { ElavonPaymentIframe } from "@common/PaymentIframe/elavon/ElavonPaymentIframe";
import { getOneOffPaymentHppApiRoute as getElavonOneOffPaymentHppApiRoute } from "@api-routes/elavonFacade";
import { getOneOffPaymentHppApiRoute, payWithSavedCardOnceOffPaymentApiRoute } from "@api-routes/eirStancerFacade";
import { PaymentChoice } from "@common/PaymentChoice/PaymentChoice";
import { IPaymentMethod } from "@features/Payment/interface/IPaymentMethod";
import { getSimplePaymentMethodCardList } from "@common/SimplePaymentMethodCardList";

interface IParams {
    amountToPay: number;
    isNewPaymentProvider: boolean;
    paymentMethodList: IPaymentMethod[];
    openedIframe?: EPaymentIframes;
    getCurrentBalance(): void;
    setOpenedIframe(openedIframe?: EPaymentIframes): void;
    getPayerDetails(): void;
}

export const PaymentBalanceIframe = (props: IParams) => {
    const {
        getCurrentBalance,
        amountToPay,
        openedIframe,
        setOpenedIframe,
        isNewPaymentProvider,
        paymentMethodList,
        getPayerDetails,
    } = props;
    const {
        amountToSend,
        successCallback,
        formMethods,
        handleRangePriceValue,
        loadPaymentIframe,
    } = usePaymentBalanceIframe({ amountToPay, getCurrentBalance, setOpenedIframe, getPayerDetails });

    const { register, errors, setValue, watch } = formMethods;

    const { t } = useTranslation(["payment", "common", "validation"]);

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        let newValue = "";
        if (value) {
            const index = value.includes(".") ? value.indexOf(".") : value.length;
            newValue = value.slice(0, index + 3);
        }
        setValue("balance", newValue);
    };

    const balance = watch("balance");

    return (
        <>
            {openedIframe !== EPaymentIframes.BALANCE && (
                <form onSubmit={loadPaymentIframe}>
                    <Grid alignItems="center" container direction="column" justifyContent="center" spacing={2}>
                        <Grid item md={2} style={{ flex: "1 1 auto" }} xs={7}>
                            <TextField
                                aria-label="amount"
                                className="balance_input"
                                error={Boolean(errors.balance)}
                                fullWidth
                                helperText={errors.balance?.message}
                                id="balanceField"
                                inputProps={{ "data-testid": "input-amount" }}
                                inputRef={register({
                                    required: t("validation:required"),
                                    validate: handleRangePriceValue,
                                })}
                                margin="dense"
                                name="balance"
                                type="string"
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item md={3} sm={4} style={{ flex: "1 1 auto" }} xs={6}>
                            <Button
                                color="secondary"
                                disabled={Boolean(errors.balance)}
                                fullWidth
                                size="large"
                                type="submit"
                                variant="contained"
                            >
                                {t("payment:payNow")}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            )}
            {openedIframe === EPaymentIframes.BALANCE && (
                <Stack>
                    <div>
                        <Typography align="center" gutterBottom variant="normal">
                            {t("payment:goingPay")}&nbsp;
                            {t("common:price", { price: priceToCents(balance ?? "0") })}
                        </Typography>
                    </div>
                    <div>
                        {isNewPaymentProvider ? (
                            <PaymentChoice
                                amount={amountToSend}
                                iframePaymentApiRoute={getOneOffPaymentHppApiRoute(amountToSend)}
                                payWithSavedCardApiRoute={(cardId) => payWithSavedCardOnceOffPaymentApiRoute(cardId)}
                                paymentMethodCardList={getSimplePaymentMethodCardList(paymentMethodList)}
                                successCallback={successCallback}
                                validatePaymentApiRoute={getOneOffPaymentHppApiRoute()}
                            />
                        ) : (
                            <ElavonPaymentIframe
                                iframePaymentApiRoute={getElavonOneOffPaymentHppApiRoute(amountToSend)}
                                successCallback={successCallback}
                                validatePaymentApiRoute={getElavonOneOffPaymentHppApiRoute()}
                            />
                        )}
                    </div>
                    <div>
                        <Button
                            color="secondary"
                            fullWidth
                            size="large"
                            variant="contained"
                            onClick={() => setOpenedIframe(undefined)}
                        >
                            {t("common:cancel")}
                        </Button>
                    </div>
                </Stack>
            )}
        </>
    );
};
